/**
 * Trustpilot REST Helpers (Story 23.3 AC #4, #5, #12)
 *
 * Per-endpoint typed wrappers around `safeFetchTrustpilot`. Each helper:
 *   - Asserts a non-empty businessUnitId (when scoped)
 *   - URL-encodes the domain query parameter (Round 3 Red Team
 *     URL-injection defense — the `find` endpoint is the ONE unscoped
 *     exception)
 *   - Passes the optional `expectedResultPath` for response-shape
 *     assertion (carries forward CallRail 22.1 + Semrush 20.4 + Moz 20.7
 *     `expectedResultPath` invariant)
 *   - Threads the THREE decrypted secrets into `safeFetchTrustpilot`'s
 *     redaction context
 *
 * TWO base URL constants (Story 35.3 expansion):
 *   - `TRUSTPILOT_API_BASE = "https://api.trustpilot.com/v1"` (Story 23.3)
 *   - `TRUSTPILOT_INVITATIONS_API_BASE = "https://invitations-api.trustpilot.com/v1"` (Story 35.3)
 *
 * Per Round 2 Pre-mortem E + Story 35.3 AC #1 — the two hosts have
 * INDEPENDENT rate-limit budgets per Trustpilot's documented per-host
 * 833/5min ceiling. A 429 on one MUST NOT poison the other's counter.
 * See `safe-fetch.ts` for the per-host counter Map.
 */
import type { AdapterResult } from "../adapter";
import { adapterErr, adapterOk } from "../adapter";

import { buildTrustpilotAdapterError } from "./errors";
import {
  coerceTrustpilotReviewId,
  safeFetchTrustpilot,
  type TrustpilotSafeFetchContext,
} from "./safe-fetch";

/** Primary base URL — Service Reviews + Business Units (Story 23.3). */
export const TRUSTPILOT_API_BASE = "https://api.trustpilot.com/v1";

/**
 * Invitations API base URL — Story 35.3 NEW (AC #1).
 *
 * The Invitations API lives on a SEPARATE host from `api.trustpilot.com`.
 * Per [Trustpilot Invitations API](https://documentation-apidocumentation.trustpilot.com/invitation-api)
 * (and verified via the documented `invitation-data/delete` endpoint at
 * `invitations-api.trustpilot.com/v1/private/business-units/{id}/invitation-data/delete`,
 * confirmed via `developers.trustpilot.com/invitations-api-overview/` 2026-05-21).
 *
 * **CRITICAL invariants** (Story 35.3 AC #1):
 *   1. Per-host rate-limit budgets are INDEPENDENT — see
 *      `safe-fetch.ts:hostCounters` Map. A 429 on this host MUST NOT
 *      increment the `api.trustpilot.com` counter.
 *   2. 30-day retention cap on invitation data — pre-flight via
 *      `validateTrustpilotInvitationsDateRange` in `retention.ts`.
 *      DIVERGENT from `TRUSTPILOT_API_BASE` which has NO retention cap
 *      (Story 23.3 AC #4 invariant carry-forward).
 *   3. Bearer OAuth required — Plus-tier-gated via the existing
 *      `privateApiEntitled` flag (no second entitlement probe).
 */
export const TRUSTPILOT_INVITATIONS_API_BASE =
  "https://invitations-api.trustpilot.com/v1";

/** Trustpilot Business Unit ID — 24-character alphanumeric per API responses. */
const BUSINESS_UNIT_ID_REGEX = /^[a-zA-Z0-9]{24}$/;

/**
 * Validate a businessUnitId synchronously. Throws a `TypeError` at the
 * helper level (NOT an adapter error) per AC #12 — these IDs come from
 * `integrations.account_id` and a missing/malformed value indicates a
 * code bug, not a transient API issue.
 */
function assertBusinessUnitId(businessUnitId: string): void {
  if (!businessUnitId || typeof businessUnitId !== "string") {
    throw new TypeError(
      "Trustpilot helpers require a businessUnitId — got empty or non-string value."
    );
  }
  if (!BUSINESS_UNIT_ID_REGEX.test(businessUnitId)) {
    throw new TypeError(
      `Trustpilot businessUnitId failed shape validation (expected 24-char alphanumeric): "${businessUnitId}"`
    );
  }
}

// ============================================================================
// Business Unit Find — `GET /v1/business-units/find?name={domain}` (public,
// API-Key auth). The ONLY unscoped endpoint — consumed ONLY at connect time.
// ============================================================================

export interface TrustpilotBusinessUnit {
  readonly id: string;
  readonly displayName: string;
  readonly country?: string;
  readonly numberOfReviews?: { readonly total: number };
}

export interface TrustpilotFindBusinessUnitResult {
  /** Empty array when find returns 0 — caller maps to `domain_not_found`. */
  readonly businessUnits: ReadonlyArray<TrustpilotBusinessUnit>;
  /** True when exactly 1 BU matched — Step 2 wizard auto-skips. */
  readonly autoPick: boolean;
}

/**
 * Resolve a domain (e.g. "greenlifewellness.com") to a Trustpilot Business
 * Unit. Public-API auth via `apikey: <KEY>` header per
 * `developers.trustpilot.com/business-units-api-(public)/` verified 2026-05-16.
 *
 * URL-encodes the domain (Round 3 Red Team URL-injection defense — a domain
 * like `foo.com?admin=1` would otherwise smuggle an extra query param).
 *
 * Returns:
 *  - `success: true, data.businessUnits: []` on 200 with empty list — caller
 *    surfaces "domain not found" user-facing copy
 *  - `success: true, data.businessUnits: [one]` with `autoPick: true` when
 *    exactly 1 BU matched (Round 4 Acceptance Auditor finding (a))
 *  - `success: true, data.businessUnits: [...many]` with `autoPick: false`
 *    when multiple BUs matched (Step 2 picker)
 *  - `success: false` on 401/403/5xx — error is sanitized with the
 *    decrypted API Key redacted from any log message
 *
 * **NOTE: the Trustpilot v1 `find` endpoint returns a SINGLE business unit
 * object directly (not wrapped in `{ businessUnits: [...] }`) when the
 * domain matches one Business Unit. We normalize both shapes to an array
 * for caller simplicity. When the domain is not found Trustpilot returns
 * HTTP 404, which `safeFetch` maps to `account_not_found` — we catch that
 * specifically and convert to an empty array success.
 */
export async function findTrustpilotBusinessUnitByDomain(
  domain: string,
  apiKey: string
): Promise<AdapterResult<TrustpilotFindBusinessUnitResult>> {
  if (!domain || typeof domain !== "string") {
    throw new TypeError("findTrustpilotBusinessUnitByDomain requires a non-empty domain string.");
  }
  const encoded = encodeURIComponent(domain);
  const url = `${TRUSTPILOT_API_BASE}/business-units/find?name=${encoded}`;
  const result = await safeFetchTrustpilot(
    url,
    {
      method: "GET",
      headers: { apikey: apiKey, Accept: "application/json" },
    },
    { decryptedApiKey: apiKey }
  );

  if (!result.success) {
    // 404 from `find` = domain not registered. Convert to success with
    // empty list so the route can emit `domain_not_found` user-facing copy
    // (distinct from a server outage).
    if (result.error.providerStatus === 404) {
      return adapterOk({ businessUnits: [], autoPick: false });
    }
    return result;
  }

  let payload: unknown;
  try {
    payload = await result.data.json();
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Trustpilot find-business-unit response was not valid JSON.",
      retryable: false,
    });
  }

  const units = parseBusinessUnitsResponse(payload);
  return adapterOk({
    businessUnits: units,
    autoPick: units.length === 1,
  });
}

function parseBusinessUnitsResponse(payload: unknown): TrustpilotBusinessUnit[] {
  if (!payload || typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;

  // Shape 1: { businessUnits: [...] }
  if (Array.isArray(obj.businessUnits)) {
    return obj.businessUnits
      .filter((u): u is Record<string, unknown> => !!u && typeof u === "object")
      .map(coerceBusinessUnit);
  }

  // Shape 2: a single business unit object (most common from /find)
  if (typeof obj.id === "string") {
    return [coerceBusinessUnit(obj)];
  }

  return [];
}

function coerceBusinessUnit(raw: Record<string, unknown>): TrustpilotBusinessUnit {
  const numberOfReviews =
    typeof raw.numberOfReviews === "object" && raw.numberOfReviews !== null
      ? (raw.numberOfReviews as Record<string, unknown>)
      : undefined;
  return {
    id: typeof raw.id === "string" ? raw.id : "",
    displayName: typeof raw.displayName === "string" ? raw.displayName : "",
    country: typeof raw.country === "string" ? raw.country : undefined,
    numberOfReviews:
      numberOfReviews && typeof numberOfReviews.total === "number"
        ? { total: numberOfReviews.total }
        : undefined,
  };
}

// ============================================================================
// Business Unit Details — `GET /v1/business-units/{businessUnitId}` (public,
// API-Key auth). Marquee headline KPIs row.
// ============================================================================

export type TrustpilotBusinessUnitDetails = Record<string, unknown>;

/**
 * Fetch the marquee Business Unit details row.
 *
 * The `auth` parameter accepts EITHER `{ apiKey }` (used at probe/connect
 * time when only the raw API Key is in scope) OR `{ accessToken }` (used
 * by the cron-driven sync path where only the Bearer access token is in
 * scope). Trustpilot's public BU endpoint accepts both auth shapes per
 * developers.trustpilot.com/business-units-api verified 2026-05-16.
 *
 * Pass 1 Blind Hunter caught a bug where the cron path was passing the
 * Bearer access token as the `apikey:` header value — Trustpilot rejects
 * that combination with 401. Splitting the auth parameter makes the
 * required secret unambiguous.
 */
export async function fetchTrustpilotBusinessUnitDetails(
  businessUnitId: string,
  auth: { apiKey: string } | { accessToken: string },
  context: TrustpilotSafeFetchContext = {}
): Promise<AdapterResult<TrustpilotBusinessUnitDetails>> {
  assertBusinessUnitId(businessUnitId);
  const url = `${TRUSTPILOT_API_BASE}/business-units/${businessUnitId}`;
  const headers: Record<string, string> = { Accept: "application/json" };
  // Pass 2 fix — use value-truthy discriminator (not `"apiKey" in auth`)
  // because a partial-type spread like `{ apiKey: undefined, accessToken:
  // "..." }` makes `apiKey` a present-but-undefined property, passing the
  // `in` check but landing in the apikey-header branch with no value — and
  // critically losing the `decryptedAccessToken` redaction context.
  const useApiKey =
    "apiKey" in auth && typeof auth.apiKey === "string" && auth.apiKey.length > 0;
  const fetchContext: TrustpilotSafeFetchContext = useApiKey
    ? { ...context, decryptedApiKey: (auth as { apiKey: string }).apiKey }
    : {
        ...context,
        decryptedAccessToken: (auth as { accessToken: string }).accessToken,
      };
  if (useApiKey) {
    headers.apikey = (auth as { apiKey: string }).apiKey;
  } else {
    headers.Authorization = `Bearer ${(auth as { accessToken: string }).accessToken}`;
  }
  const result = await safeFetchTrustpilot(
    url,
    { method: "GET", headers },
    fetchContext
  );

  if (!result.success) return result;

  try {
    const payload = (await result.data.json()) as Record<string, unknown>;
    if (typeof payload !== "object" || payload === null) {
      return adapterErr({
        code: "invalid_response",
        message: "Trustpilot business-unit-details response was not a JSON object.",
        retryable: false,
      });
    }
    return adapterOk(payload);
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Trustpilot business-unit-details response was not valid JSON.",
      retryable: false,
    });
  }
}

// ============================================================================
// Public Service Reviews — `GET /v1/business-units/{id}/reviews` (public,
// API-Key auth). Per-review records, max 100 per page (Story 23.3 fetches
// page 1 only — pagination deferred per AC #4).
// ============================================================================

export interface TrustpilotPublicReviewsResult {
  readonly reviews: ReadonlyArray<Record<string, unknown>>;
}

/**
 * Fetch a page of public Service Reviews. Like
 * `fetchTrustpilotBusinessUnitDetails`, accepts either auth shape — apiKey
 * at probe/connect time, accessToken at cron-sync time.
 */
export async function fetchTrustpilotPublicReviews(
  businessUnitId: string,
  auth: { apiKey: string } | { accessToken: string },
  options: { page?: number; perPage?: number } = {},
  context: TrustpilotSafeFetchContext = {}
): Promise<AdapterResult<TrustpilotPublicReviewsResult>> {
  assertBusinessUnitId(businessUnitId);
  const page = options.page ?? 1;
  const perPage = Math.min(options.perPage ?? 100, 100);
  const url = `${TRUSTPILOT_API_BASE}/business-units/${businessUnitId}/reviews?perPage=${perPage}&page=${page}`;
  const headers: Record<string, string> = { Accept: "application/json" };
  // Pass 2 fix — value-truthy discriminator (see fetchTrustpilotBusinessUnitDetails).
  const useApiKey =
    "apiKey" in auth && typeof auth.apiKey === "string" && auth.apiKey.length > 0;
  const fetchContext: TrustpilotSafeFetchContext = useApiKey
    ? { ...context, decryptedApiKey: (auth as { apiKey: string }).apiKey }
    : {
        ...context,
        decryptedAccessToken: (auth as { accessToken: string }).accessToken,
      };
  if (useApiKey) {
    headers.apikey = (auth as { apiKey: string }).apiKey;
  } else {
    headers.Authorization = `Bearer ${(auth as { accessToken: string }).accessToken}`;
  }
  const result = await safeFetchTrustpilot(
    url,
    { method: "GET", headers },
    fetchContext
  );

  if (!result.success) return result;

  try {
    const payload = (await result.data.json()) as Record<string, unknown>;
    const reviews = Array.isArray(payload.reviews)
      ? (payload.reviews as ReadonlyArray<Record<string, unknown>>)
      : null;
    if (!reviews) {
      return adapterErr({
        code: "invalid_response",
        message:
          "Trustpilot service-reviews response missing `reviews` array — shape mismatch.",
        retryable: false,
      });
    }
    return adapterOk({ reviews });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Trustpilot service-reviews response was not valid JSON.",
      retryable: false,
    });
  }
}

// ============================================================================
// Private Service Reviews — `GET /v1/private/business-units/{id}/reviews`
// (paid tier — Bearer OAuth required). Adds 8 additional fields to the
// public review shape.
// ============================================================================

export async function fetchTrustpilotPrivateReviews(
  businessUnitId: string,
  accessToken: string,
  options: { page?: number; perPage?: number } = {},
  context: TrustpilotSafeFetchContext = {}
): Promise<AdapterResult<TrustpilotPublicReviewsResult>> {
  assertBusinessUnitId(businessUnitId);
  const page = options.page ?? 1;
  const perPage = Math.min(options.perPage ?? 100, 100);
  const url = `${TRUSTPILOT_API_BASE}/private/business-units/${businessUnitId}/reviews?perPage=${perPage}&page=${page}`;
  const result = await safeFetchTrustpilot(
    url,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
    { decryptedAccessToken: accessToken, ...context }
  );

  if (!result.success) return result;

  try {
    const payload = (await result.data.json()) as Record<string, unknown>;
    const reviews = Array.isArray(payload.reviews)
      ? (payload.reviews as ReadonlyArray<Record<string, unknown>>)
      : null;
    if (!reviews) {
      return adapterErr({
        code: "invalid_response",
        message:
          "Trustpilot private service-reviews response missing `reviews` array — shape mismatch.",
        retryable: false,
      });
    }
    return adapterOk({ reviews });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Trustpilot private service-reviews response was not valid JSON.",
      retryable: false,
    });
  }
}

/**
 * Allowlist of fields that ONLY the private endpoint surfaces. The merge
 * picks ONLY these fields from the private record to overlay on the
 * public base — never letting private mutate `stars` / `title` / `text` /
 * `createdAt` etc. that both endpoints emit. Trustpilot's private endpoint
 * has been observed to return slightly different copies of common fields
 * (post-moderation public vs original-submitted private) and last-write-
 * wins merging would silently corrupt customer-facing aggregations.
 */
const TRUSTPILOT_PRIVATE_OVERLAY_FIELDS: ReadonlyArray<string> = [
  "referenceId",
  "referralEmail",
  "source",
  "status",
  "tags",
  "experiencedAt",
  "location",
  "findReviewer",
  "countsTowardsTrustScore",
  "countsTowardsLocationTrustScore",
  // Story 35.3 Batch 1 — additional private-tier fields per AC #5.
  // The transformer projects these onto the per-review record only when
  // `privateApiEntitled === true`.
  "invitation",
  "businessUnitHistory",
];

function pickPrivateOverlay(
  priv: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of TRUSTPILOT_PRIVATE_OVERLAY_FIELDS) {
    if (key in priv) out[key] = priv[key];
  }
  return out;
}

/**
 * Merge public + private review records by `id`. The public list is the
 * source of truth for the base shared fields (stars/title/text/etc.); the
 * private list adds only the 10 private-tier fields per the allowlist
 * above. Iterates the UNION of IDs so private-only reviews (e.g.,
 * status='reported' or pending-moderation) still surface on the paid
 * tier — Round 1 Edge Hunter finding.
 *
 * When private is null/empty (free tier), the merge is a no-op
 * pass-through.
 */
export function mergeTrustpilotPublicAndPrivateReviews(
  publicReviews: ReadonlyArray<Record<string, unknown>>,
  privateReviews: ReadonlyArray<Record<string, unknown>> | null
): ReadonlyArray<Record<string, unknown>> {
  if (!privateReviews || privateReviews.length === 0) {
    return publicReviews;
  }
  // Round 2 fix R2-7 — use `coerceReviewId` so numeric IDs (Trustpilot
  // shape drift per round 1 fix #23) still get merged. The previous
  // `typeof r.id === "string"` check silently dropped numeric-ID records
  // from the merge, undercounting paid-tier aggregates.
  const privateMap = new Map<string, Record<string, unknown>>();
  for (const r of privateReviews) {
    const id = coerceReviewId(r.id);
    if (id) privateMap.set(id, r);
  }

  // Union of IDs — preserves public ordering, then appends private-only.
  const seen = new Set<string>();
  const merged: Record<string, unknown>[] = [];
  for (const pub of publicReviews) {
    const id = coerceReviewId(pub.id);
    if (!id) {
      merged.push(pub);
      continue;
    }
    seen.add(id);
    const priv = privateMap.get(id);
    if (!priv) {
      merged.push(pub);
      continue;
    }
    merged.push({ ...pub, ...pickPrivateOverlay(priv) });
  }
  // Private-only reviews (paid-tier moderation / reported / pending state).
  for (const priv of privateReviews) {
    const id = coerceReviewId(priv.id);
    if (!id || seen.has(id)) continue;
    merged.push(priv);
  }
  return merged;
}

// ============================================================================
// Story 35.3 — Invitations API helper (Endpoint Group D)
// ============================================================================

/**
 * Documented top-level keys from the Trustpilot Invitations API
 * `invitation-data` endpoint. Story 35.3 AC #6.
 *
 * The set is used by `fetchTrustpilotInvitationData` to validate the
 * response shape — if NONE of these keys are present in the response
 * object, we treat it as a shape mismatch. Per AC #6 we DO NOT throw on
 * shape drift; we let the transformer degrade to NULL on individual
 * missing keys.
 */
const INVITATION_DATA_DOCUMENTED_KEYS: ReadonlyArray<string> = [
  "total_invitations_sent",
  "total_invitations_opened",
  "total_invitations_clicked",
  "total_invitations_bounced",
  "total_invitations_unsubscribed",
  "total_invitations_converted",
  "templates",
  "invitation_links",
];

/** Raw response shape from the Invitations API endpoint. */
export type TrustpilotInvitationData = Record<string, unknown>;

/**
 * Fetch the Invitations API aggregate counts for a single business unit
 * over a date range.
 *
 * Story 35.3 AC #10:
 *   - Endpoint: `GET https://invitations-api.trustpilot.com/v1/private/
 *     business-units/{businessUnitId}/invitation-data/?startDate={ISO}&
 *     endDate={ISO}` (the SECOND base URL).
 *   - Auth: `Authorization: Bearer <accessToken>` — Plus-tier-gated.
 *   - URL-encodes startDate + endDate (Round 3 Red Team URL-injection
 *     defense — Story 23.3 Task 2.4 invariant carry-forward).
 *   - The response shape is a flat object (NOT wrapped in a top-level
 *     array); we validate by checking that at least ONE documented key
 *     is present. AC #6: on full shape mismatch the adapter emits a
 *     `trustpilot_invitations_shape_drift` log and degrades to NULL on
 *     individual fields — does NOT throw.
 *
 * Returns the raw response as `TrustpilotInvitationData` —
 * `transformTrustpilotInvitationData` does the wire-format → flat-record
 * projection downstream.
 */
export async function fetchTrustpilotInvitationData(
  businessUnitId: string,
  accessToken: string,
  startDate: Date,
  endDate: Date,
  context: TrustpilotSafeFetchContext = {}
): Promise<AdapterResult<TrustpilotInvitationData>> {
  assertBusinessUnitId(businessUnitId);
  if (
    !Number.isFinite(startDate.getTime()) ||
    !Number.isFinite(endDate.getTime())
  ) {
    throw new TypeError(
      "fetchTrustpilotInvitationData requires finite startDate and endDate"
    );
  }
  // Code-review round 1 fix #4 — defense-in-depth against direct callers
  // bypassing the `validateTrustpilotInvitationsDateRange` pre-flight.
  // Inverted ranges would otherwise smuggle malformed URLs to Trustpilot.
  if (startDate.getTime() > endDate.getTime()) {
    throw new TypeError(
      `fetchTrustpilotInvitationData: startDate (${startDate.toISOString()}) must be on or before endDate (${endDate.toISOString()})`
    );
  }
  const encodedStart = encodeURIComponent(startDate.toISOString());
  const encodedEnd = encodeURIComponent(endDate.toISOString());
  const url =
    `${TRUSTPILOT_INVITATIONS_API_BASE}/private/business-units/${businessUnitId}/invitation-data/` +
    `?startDate=${encodedStart}&endDate=${encodedEnd}`;
  const result = await safeFetchTrustpilot(
    url,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
    { decryptedAccessToken: accessToken, ...context }
  );

  if (!result.success) return result;

  let payload: unknown;
  try {
    payload = await result.data.json();
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "Trustpilot invitation-data response was not valid JSON.",
      retryable: false,
    });
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return adapterErr({
      code: "invalid_response",
      message:
        "Trustpilot invitation-data response was not a JSON object (expected flat object with documented keys).",
      retryable: false,
    });
  }

  // Story 35.3 AC #6 — at least one documented key MUST be present.
  // Full shape mismatch is logged + degraded by the caller; we surface
  // it as an `invalid_response` only when NONE of the 8 keys exist
  // (more defensive than the AC asks; aligns with the "shape-drift
  // defense must never break the broader sync flow" Round 4 invariant
  // — caller handles this `invalid_response` by degrading to NULL).
  //
  // Code-review round 1 fix #5 — use `Object.prototype.hasOwnProperty`
  // instead of the `in` operator so prototype-pollution payloads cannot
  // mark an undocumented response as valid via inherited properties.
  // Code-review round 1 fix #31 — additionally surface a partial-drift
  // log when SOME but not ALL documented keys are present. This is what
  // AC #6 calls "shape drift" emission; the caller's `invalid_response`
  // path only fires on full mismatch (no documented keys at all).
  const obj = payload as Record<string, unknown>;
  const presentKeys = INVITATION_DATA_DOCUMENTED_KEYS.filter((k) =>
    Object.prototype.hasOwnProperty.call(obj, k)
  );
  const hasAnyKey = presentKeys.length > 0;
  // Expose a one-shot accessor for the caller adapter to detect partial
  // drift without re-walking the response. Attached as a non-enumerable
  // property so JSON serialization (e.g. caching) is unaffected.
  if (hasAnyKey && presentKeys.length < INVITATION_DATA_DOCUMENTED_KEYS.length) {
    Object.defineProperty(obj, "__trustpilot_invitations_partial_drift__", {
      value: {
        expectedKeys: INVITATION_DATA_DOCUMENTED_KEYS,
        actualKeys: presentKeys,
      },
      enumerable: false,
      configurable: false,
      writable: false,
    });
  }
  if (!hasAnyKey) {
    return adapterErr({
      code: "invalid_response",
      message:
        "Trustpilot invitation-data response had no documented top-level keys — full shape drift.",
      retryable: false,
    });
  }

  return adapterOk(obj);
}

// ============================================================================
// Story 35.3 — Paginated Service Reviews helper (Batch 3)
// ============================================================================

/**
 * Hard cap on review pages — Story 35.3 AC #1 worst-case subrequest
 * budget invariant (6 pages * 100 reviews = 600 reviews max, well
 * under Cloudflare Workers' 50-subrequest cap).
 */
export const TRUSTPILOT_MAX_REVIEW_PAGES = 6;

/** Per-page review count — Trustpilot's documented `perPage` ceiling. */
const TRUSTPILOT_REVIEWS_PAGE_SIZE = 100;

export interface TrustpilotPaginatedReviewsResult {
  /** Merged reviews across ALL fetched pages (deduplicated by `id`). */
  readonly reviews: ReadonlyArray<Record<string, unknown>>;
  /** Number of pages successfully fetched (1..TRUSTPILOT_MAX_REVIEW_PAGES). */
  readonly pageCount: number;
  /**
   * True when the LAST page fetched returned a full set (== perPage) —
   * indicates Trustpilot has MORE reviews beyond what we fetched.
   * Surfaces in widgets as "showing first 600 reviews" footnote.
   */
  readonly capped: boolean;
  /**
   * Code-review round 1 fix #17 — populated when pagination encountered
   * an error mid-stream. The `reviews` array still contains the partial
   * data from successfully-fetched pages; the caller can ship the
   * partial set with a forensic-log signal. NULL when all requested
   * pages succeeded.
   */
  readonly partialError?: {
    readonly code: string;
    readonly providerStatus?: number;
    readonly failedAtPage: number;
  };
}

/**
 * Fetch N pages of public Service Reviews and merge. Story 35.3 AC #10.
 *
 * Loops `fetchTrustpilotPublicReviews` from `startPage` for up to
 * `maxPages` pages (capped at 6). Stops early when a page returns
 * fewer than `perPage` reviews (no more data). Deduplicates by
 * `review.id` defensively — Trustpilot's pagination is `createdAt`-
 * ordered + stable, but a review created between two page calls could
 * theoretically duplicate.
 *
 * On per-page error: returns the partial set + propagates the page's
 * error via `adapterErr`. The caller (`fetchMetrics`) is responsible
 * for the Round 5 Edge Case Hunter invariant — a failure mid-stream
 * SHOULD NOT cascade to the other endpoint groups.
 *
 * The `auth` shape matches `fetchTrustpilotPublicReviews` — Bearer
 * access token at cron-sync time (or apiKey at probe time, though
 * Batch 3 only ever runs from fetchMetrics).
 */
export async function fetchTrustpilotPaginatedReviews(
  businessUnitId: string,
  auth: { apiKey: string } | { accessToken: string },
  options: {
    readonly startPage?: number;
    readonly maxPages?: number;
    readonly perPage?: number;
  } = {},
  context: TrustpilotSafeFetchContext = {}
): Promise<AdapterResult<TrustpilotPaginatedReviewsResult>> {
  assertBusinessUnitId(businessUnitId);
  const startPage = options.startPage ?? 1;
  if (startPage < 1) {
    throw new TypeError(
      `fetchTrustpilotPaginatedReviews: startPage must be >= 1 (got ${startPage})`
    );
  }
  const requestedMax = options.maxPages ?? 1;
  const effectiveMax = Math.min(
    Math.max(1, Math.floor(requestedMax)),
    TRUSTPILOT_MAX_REVIEW_PAGES
  );
  const perPage = Math.min(
    Math.max(1, Math.floor(options.perPage ?? TRUSTPILOT_REVIEWS_PAGE_SIZE)),
    TRUSTPILOT_REVIEWS_PAGE_SIZE
  );

  const merged: Record<string, unknown>[] = [];
  const seenIds = new Set<string>();
  let pageCount = 0;
  let capped = false;

  for (let pageOffset = 0; pageOffset < effectiveMax; pageOffset++) {
    const pageNumber = startPage + pageOffset;
    const result = await fetchTrustpilotPublicReviews(
      businessUnitId,
      auth,
      { page: pageNumber, perPage },
      context
    );
    if (!result.success) {
      // Code-review round 1 fix #17 — when pagination fails mid-stream
      // we return SUCCESS with the partial-set + a `partialError` field
      // (instead of dropping all already-merged pages into a useless
      // error path). This preserves the AC #21 invariant ("a failure
      // in one endpoint group MUST NOT cascade") AND lets the caller
      // ship the partial in-period aggregates already computed.
      // Round 2 fix R2-13 — removed the shadow `pageNumber` declaration;
      // re-use the outer `pageNumber` from line above.
      return adapterOk({
        reviews: merged,
        pageCount,
        capped: false, // partial-fail → we don't know if more data exists past failure point
        partialError: {
          code: result.error.code,
          providerStatus: result.error.providerStatus,
          failedAtPage: pageNumber,
        },
      });
    }
    const pageReviews = result.data.reviews;
    // Code-review round 1 fix #24 — don't count an empty page in
    // `pageCount`. An immediately-empty first page returned `pageCount=1`
    // before; now returns `pageCount=0` to honestly reflect "0 data pages
    // fetched". This also avoids a spurious +1 in the `requestsConsumed`
    // forensic log from the calling adapter.
    if (pageReviews.length > 0) {
      pageCount++;
    }
    for (const r of pageReviews) {
      // Code-review round 1 fix #23 — coerce id to string so numeric
      // IDs (Trustpilot shape drift) still get deduped.
      // Round 2 fix R2-12 — records with no valid id can't be safely
      // deduped across pages. Drop them defensively + record a forensic
      // count for the caller (returned via the `partialError` channel
      // when applicable) to avoid double-counting on retries.
      const id = coerceReviewId(r.id);
      if (!id) continue; // drop unidentifiable record
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      merged.push(r);
    }
    // If the page returned fewer than `perPage` records → no more data.
    if (pageReviews.length < perPage) break;
    // If we just consumed our last allowed page AND it was full → caller
    // would have hit pageOffset+1 === effectiveMax; record capped flag.
    if (pageOffset === effectiveMax - 1 && pageReviews.length === perPage) {
      capped = true;
    }
  }

  return adapterOk({ reviews: merged, pageCount, capped });
}

/**
 * Round 2 fix R2-11 — `coerceReviewId` removed. Use the shared
 * `coerceTrustpilotReviewId` from `./safe-fetch` for all dedup keys.
 */
const coerceReviewId = coerceTrustpilotReviewId;

/**
 * Suppress unused-import lint for `buildTrustpilotAdapterError` — it's
 * re-exported below and used by callers.
 */
export { buildTrustpilotAdapterError };
